import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useProject } from '../hooks/use-projects';

interface ProjectWithMembers {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  status: 'active' | 'archived';
  activeFlowchartId: string | null;
  members: Array<{
    userId: string;
    projectRole: 'manager' | 'member' | 'viewer';
    user: { id: string; fullName: string; email: string; avatarUrl: string | null };
  }>;
}

export function ProjectDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const project = useProject(id);
  const { t } = useTranslation();

  if (project.isLoading) return <Skeleton className="h-40" />;
  const data = project.data as ProjectWithMembers | undefined;
  if (!data) return <p className="text-sm text-surface-500">{t('project.notFound')}</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: data.color }} />
            <h1 className="text-2xl font-semibold">{data.name}</h1>
          </div>
          <p className="mt-1 text-sm text-surface-500">{data.description ?? '—'}</p>
        </div>
        <Button asChild>
          <Link to={`/projects/${data.id}/board`}>{t('project.openBoard')}</Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('project.members', { count: data.members.length })}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm">
              {data.members.map((m) => (
                <li key={m.userId} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{m.user.fullName || m.user.email}</p>
                    <p className="text-xs text-surface-500">{m.user.email}</p>
                  </div>
                  <span className="rounded-md bg-surface-100 px-2 py-1 text-xs uppercase tracking-wide text-surface-600">
                    {m.projectRole}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('project.pipelineSummary')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-surface-500">{t('project.stageAnalyticsNote')}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
